import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BookOpen, Plus, LogOut, Users, FileText, Calendar, Sparkles, Video, BookMarked, ChevronRight, Settings, User as UserIcon, Compass, Clock, Mic, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import SermonCard from '@/components/SermonCard';
import AppTutorial from '@/components/AppTutorial';
import SermonProfileQuestionnaire from '@/components/SermonProfileQuestionnaire';
import NotesManager from '@/components/NotesManager';
import NextStepNavigator from '@/components/NextStepNavigator';
import PreachingCalendar from '@/components/PreachingCalendar';
import UpgradePrompt from '@/components/UpgradePrompt';
import NotificationsDropdown from '@/components/NotificationsDropdown';
import logo from '@/assets/logo.png';
import { apiCache } from '@/utils/cache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showSermonProfile, setShowSermonProfile] = useState(false);
  const [showNotesManager, setShowNotesManager] = useState(false);
  const [showNextStepNavigator, setShowNextStepNavigator] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedSermonForNextStep, setSelectedSermonForNextStep] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  useEffect(() => {
    fetchSermons();
    fetchSubscriptionStatus();
    fetchTrialStatus();
    checkFirstTimeUser();
  }, []);

  const checkFirstTimeUser = () => {
    // Check if sermon profile is completed
    if (!user?.sermon_profile_completed) {
      setTimeout(() => {
        setShowSermonProfile(true);
      }, 800);
      return;
    }
    
    // Then check tutorial
    const tutorialCompleted = localStorage.getItem('tutorial_completed');
    if (!tutorialCompleted) {
      // Small delay to let the page render first
      setTimeout(() => {
        setShowTutorial(true);
      }, 500);
    }
  };

  const fetchTrialStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/subscription/trial-status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setTrialStatus(response.data);
      
      // Show upgrade prompt if needed
      if (response.data.needs_upgrade) {
        // Delay to avoid showing multiple modals at once
        setTimeout(() => {
          setShowUpgradePrompt(true);
        }, 1500);
      }
    } catch (error) {
      console.error('Error fetching trial status:', error);
    }
  };

  const fetchSermons = async () => {
    try {
      // Check cache first
      const cacheKey = `sermons_${user.id}`;
      const cached = apiCache.get(cacheKey);
      if (cached) {
        setSermons(cached);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/sermons`);
      setSermons(response.data);
      apiCache.set(cacheKey, response.data, 2 * 60 * 1000); // Cache for 2 minutes
    } catch (error) {
      toast.error('Failed to load sermons');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      // Check cache first
      const cacheKey = `subscription_${user.id}`;
      const cached = apiCache.get(cacheKey);
      if (cached) {
        setSubscriptionStatus(cached);
        return;
      }

      const response = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(response.data);
      apiCache.set(cacheKey, response.data, 5 * 60 * 1000); // Cache for 5 minutes
    } catch (error) {
      console.error('Failed to load subscription status');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      // Clear all auth data from localStorage
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      setUser(null);
      // Use full page reload to ensure clean state
      window.location.href = '/';
    } catch (error) {
      // Even if logout API fails, clear local state
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      setUser(null);
      window.location.href = '/';
    }
  };

  const handleSermonProfileComplete = (profileData) => {
    // Update user with completed profile
    const updatedUser = { ...user, sermon_profile: profileData, sermon_profile_completed: true };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setShowSermonProfile(false);
    
    // Show tutorial after profile completion
    const tutorialCompleted = localStorage.getItem('tutorial_completed');
    if (!tutorialCompleted) {
      setTimeout(() => {
        setShowTutorial(true);
      }, 500);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cloud-gray via-white to-glory-50">
      {/* Trial Status Banner */}
      {trialStatus?.is_trial && !trialStatus?.trial_expired && (
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4" />
              <span>
                <strong>{trialStatus.days_remaining} days</strong> left in your free trial
                {trialStatus.usage && (
                  <span className="hidden sm:inline">
                    {' '}• {trialStatus.usage.sermons}/{trialStatus.usage.sermons_limit} sermons used
                  </span>
                )}
              </span>
            </div>
            <Button
              onClick={() => navigate('/pricing')}
              size="sm"
              className="bg-white text-blue-600 hover:bg-blue-50 font-medium text-xs px-4"
            >
              Upgrade Now
            </Button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-pulpit-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img src={logo} alt="PulpitPilot AI Logo" className="h-10 sm:h-14 w-auto" />
              <span className="font-display text-lg sm:text-2xl font-bold text-pulpit-900">PulpitPilot AI</span>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Button
                data-testid="bible-nav-button"
                data-tutorial="bible"
                variant="ghost"
                onClick={() => navigate('/bible')}
                className="text-pulpit-900 hover:text-pulpit-700 hover:bg-cloud-gray hidden md:flex"
              >
                <BookOpen className="w-5 h-5 mr-2" />
                Bible
              </Button>
              
              <Button
                data-testid="study-guide-nav-button"
                data-tutorial="study-guide"
                variant="ghost"
                onClick={() => navigate('/study-guide')}
                className="text-pulpit-900 hover:text-pulpit-700 hover:bg-cloud-gray hidden md:flex"
              >
                <FileText className="w-5 h-5 mr-2" />
                Study Guide
              </Button>
              
              <Button
                data-testid="community-nav-button"
                data-tutorial="community"
                variant="ghost"
                onClick={() => navigate('/community')}
                className="text-pulpit-900 hover:text-pulpit-700 hover:bg-cloud-gray hidden sm:flex"
              >
                <Users className="w-5 h-5 mr-2" />
                Community
              </Button>
              
              <Button
                data-testid="community-nav-button-mobile"
                variant="ghost"
                onClick={() => navigate('/community')}
                className="text-pulpit-900 hover:text-pulpit-700 hover:bg-cloud-gray sm:hidden p-2"
              >
                <Users className="w-5 h-5" />
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    data-tutorial="account"
                    className="flex items-center space-x-2 hover:bg-cloud-gray rounded-full p-1">
                    <Avatar className="w-8 h-8 sm:w-10 sm:h-10 cursor-pointer">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-pulpit-200 text-pulpit-900">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs sm:text-sm font-medium text-gray-700 hidden md:block">{user?.name}</span>
                    <ChevronRight className="w-4 h-4 text-gray-400 hidden md:block rotate-90" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user?.name}</p>
                      <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/account')} className="cursor-pointer">
                    <UserIcon className="w-4 h-4 mr-2" />
                    My Account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/pricing')} className="cursor-pointer">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/homiletics-mentor')} className="cursor-pointer" data-testid="menu-homiletics-mentor">
                    <Compass className="w-4 h-4 mr-2" />
                    Homiletics Mentor Pathway
                  </DropdownMenuItem>
                  {user?.is_admin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer text-amber-600">
                        <Settings className="w-4 h-4 mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Notifications Bell */}
              <NotificationsDropdown user={user} />
              
              <Button
                data-testid="logout-button-mobile"
                variant="outline"
                onClick={handleLogout}
                className="border-pulpit-300 text-pulpit-700 hover:bg-cloud-gray sm:hidden p-2"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="mb-12 animate-fade-in">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-pulpit-900 mb-3">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-lg text-gray-600">
            Ready to create your next inspiring sermon?
          </p>
        </div>

        {/* Subscription Status Banner */}
        {subscriptionStatus && subscriptionStatus.tier === 'starter' && (
          <div className="mb-8 bg-gradient-to-r from-cloud-gray0 to-glory-500 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-display text-2xl font-bold mb-2 text-glory-gold">
                  You're on the Starter Plan
                </h3>
                <p className="text-glory-gold mb-3 font-medium">
                  Sermons: {subscriptionStatus.usage.sermons.used}/{subscriptionStatus.usage.sermons.limit} • 
                  Series: {subscriptionStatus.usage.series.used}/{subscriptionStatus.usage.series.limit} • 
                  Projects: {subscriptionStatus.usage.saved_projects.used}/{subscriptionStatus.usage.saved_projects.limit}
                </p>
                <p className="text-sm text-midnight-black font-medium">
                  Upgrade to Pro for unlimited sermon generations, series planning, and more!
                </p>
              </div>
              <Button
                onClick={() => navigate('/pricing')}
                className="bg-white text-pulpit-700 hover:bg-cloud-gray font-semibold px-6 py-3 rounded-full"
              >
                Upgrade Now
              </Button>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-pulpit-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Sermons</p>
                <p className="text-3xl font-bold text-pulpit-900">{sermons.length}</p>
              </div>
              <FileText className="w-12 h-12 text-pulpit-600 opacity-20" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-pulpit-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Public Sermons</p>
                <p className="text-3xl font-bold text-pulpit-900">
                  {sermons.filter(s => s.is_public).length}
                </p>
              </div>
              <Users className="w-12 h-12 text-pulpit-600 opacity-20" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-pulpit-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">This Month</p>
                <p className="text-3xl font-bold text-pulpit-900">
                  {sermons.filter(s => {
                    const date = new Date(s.created_at);
                    const now = new Date();
                    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <Calendar className="w-12 h-12 text-pulpit-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Studio Tier Feature Banner */}
        {user?.subscription_tier === 'studio' || user?.subscription_tier === 'church_teams' ? (
          <div className="mb-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
              <div>
                <h3 className="font-display text-2xl font-bold mb-2 flex items-center">
                  <Video className="w-7 h-7 mr-2" />
                  Video Studio Available!
                </h3>
                <p className="text-purple-100">
                  Transform your sermon videos into engaging social media clips with AI
                </p>
              </div>
              <Button
                data-testid="video-studio-button"
                onClick={() => navigate('/video-studio')}
                className="bg-white text-purple-700 hover:bg-purple-50 font-semibold px-6 py-3 rounded-full w-full sm:w-auto"
              >
                <Video className="w-5 h-5 mr-2" />
                Open Video Studio
              </Button>
            </div>
          </div>
        ) : null}

        {/* AI Voice Cloaking Feature Card */}
        <div className="mb-8 bg-gradient-to-r from-violet-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h3 className="font-display text-2xl font-bold mb-2 flex items-center text-white">
                <Mic className="w-7 h-7 mr-2 text-violet-200" />
                AI Voice Cloaking
              </h3>
              <p className="text-violet-100">
                Generate sermon content in your unique preaching voice and style.
              </p>
            </div>
            <Button
              data-testid="voice-cloaking-button"
              onClick={() => navigate('/voice-cloaking')}
              className="bg-white text-violet-700 hover:bg-violet-50 font-semibold px-6 py-3 rounded-full w-full sm:w-auto"
            >
              <Mic className="w-5 h-5 mr-2" />
              Write Like Me
            </Button>
          </div>
        </div>

        {/* Analytics Dashboard Card */}
        <div className="mb-8 bg-gradient-to-r from-emerald-600 to-teal-700 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h3 className="font-display text-2xl font-bold mb-2 flex items-center text-white">
                <BarChart3 className="w-7 h-7 mr-2 text-emerald-200" />
                Analytics
              </h3>
              <p className="text-emerald-100">
                Track your sermon metrics, community engagement, and voice match scores.
              </p>
            </div>
            <Button
              data-testid="analytics-button"
              onClick={() => navigate('/analytics')}
              className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-6 py-3 rounded-full w-full sm:w-auto"
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              View Analytics
            </Button>
          </div>
        </div>

        {/* NextStep Navigator Feature Card */}
        <div className="mb-8 bg-gradient-to-r from-blue-700 to-blue-900 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h3 className="font-display text-2xl font-bold mb-2 flex items-center text-white">
                <Compass className="w-7 h-7 mr-2 text-amber-400" />
                NextStep Navigator™
              </h3>
              <p className="text-gray-200">
                Never get stuck again — discover where your sermon or series should go next.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Button
                onClick={() => {
                  setSelectedSermonForNextStep(null);
                  setShowNextStepNavigator(true);
                }}
                className="bg-amber-500 text-gray-900 hover:bg-amber-400 font-semibold px-6 py-3 rounded-full w-full sm:w-auto"
              >
                <Compass className="w-5 h-5 mr-2" />
                What's Next?
              </Button>
              <Button
                onClick={() => setShowCalendar(!showCalendar)}
                variant="outline"
                className="border-white text-white hover:bg-white/20 font-semibold px-6 py-3 rounded-full w-full sm:w-auto"
              >
                <Calendar className="w-5 h-5 mr-2" />
                {showCalendar ? 'Hide Calendar' : 'Preaching Calendar'}
              </Button>
            </div>
          </div>
        </div>

        {/* Preaching Calendar (collapsible) */}
        {showCalendar && (
          <div className="mb-8">
            <PreachingCalendar user={user} />
          </div>
        )}

        {/* Community Feature Card (Pro+) */}
        {user?.subscription_tier && ['pro', 'studio', 'church_teams'].includes(user.subscription_tier.toLowerCase()) && (
          <div className="mb-8 bg-gradient-to-r from-purple-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
              <div>
                <h3 className="font-display text-2xl font-bold mb-2 flex items-center text-white">
                  <Users className="w-7 h-7 mr-2 text-purple-200" />
                  Community Hub
                </h3>
                <p className="text-purple-100">
                  Connect with fellow preachers, share sermons, and find mentors.
                </p>
              </div>
              <Button
                data-testid="community-button"
                onClick={() => navigate('/community')}
                className="bg-white text-purple-700 hover:bg-purple-50 font-semibold px-6 py-3 rounded-full w-full sm:w-auto"
              >
                <Users className="w-5 h-5 mr-2" />
                Enter Community
              </Button>
            </div>
          </div>
        )}

        {/* Church Teams Feature Card (Church Teams tier only) */}
        {user?.subscription_tier === 'church_teams' && (
          <div className="mb-8 bg-gradient-to-r from-teal-600 to-emerald-700 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
              <div>
                <h3 className="font-display text-2xl font-bold mb-2 flex items-center text-white">
                  <Users className="w-7 h-7 mr-2 text-teal-200" />
                  Church Team
                </h3>
                <p className="text-teal-100">
                  Collaborate with your teaching team, share sermons, and plan together.
                </p>
              </div>
              <Button
                data-testid="team-management-button"
                onClick={() => navigate('/team-management')}
                className="bg-white text-teal-700 hover:bg-teal-50 font-semibold px-6 py-3 rounded-full w-full sm:w-auto"
              >
                <Users className="w-5 h-5 mr-2" />
                Manage Team
              </Button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 space-y-4 sm:space-y-0">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-pulpit-900">My Sermons</h2>
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            {/* Notes Manager Button (Pro+) */}
            {user?.subscription_tier && ['pro', 'studio', 'church_teams'].includes(user.subscription_tier.toLowerCase()) && (
              <Button
                onClick={() => setShowNotesManager(true)}
                variant="outline"
                className="border-glory-400 text-glory-700 hover:bg-glory-50 px-4 sm:px-6 py-3 rounded-full font-medium w-full sm:w-auto"
              >
                <FileText className="w-5 h-5 mr-2" />
                My Notes
              </Button>
            )}
            <Button
              data-testid="create-series-button"
              onClick={() => navigate('/series/new')}
              variant="outline"
              className="border-pulpit-300 text-pulpit-700 hover:bg-cloud-gray px-4 sm:px-6 py-3 rounded-full font-medium w-full sm:w-auto"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Create Series
            </Button>
            <Button
              data-testid="create-sermon-button"
              data-tutorial="create-sermon"
              onClick={() => navigate('/sermon/new')}
              className="bg-pulpit-600 hover:bg-pulpit-700 text-white px-4 sm:px-6 py-3 rounded-full font-medium shadow-lg w-full sm:w-auto"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Sermon
            </Button>
          </div>
        </div>

        {/* Sermons List */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-xl text-gray-600">Loading sermons...</div>
          </div>
        ) : sermons.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl shadow-lg border border-pulpit-100">
            <Sparkles className="w-16 h-16 text-glory-400 mx-auto mb-4" />
            <h3 className="font-display text-2xl font-bold text-pulpit-900 mb-2">
              No sermons yet
            </h3>
            <p className="text-gray-600 mb-6">
              Start creating your first AI-powered sermon today!
            </p>
            <Button
              data-testid="empty-state-create-button"
              onClick={() => navigate('/sermon/new')}
              className="bg-pulpit-600 hover:bg-pulpit-700 text-white px-8 py-3 rounded-full font-medium"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Sermon
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sermons.map((sermon) => (
              <SermonCard 
                key={sermon.id} 
                sermon={sermon}
                user={user}
                onUpdate={fetchSermons}
                onNextStep={(sermon) => {
                  setSelectedSermonForNextStep(sermon);
                  setShowNextStepNavigator(true);
                }}
              />
            ))}
          </div>
        )}
      </main>

      {/* Sermon Profile Questionnaire */}
      {showSermonProfile && (
        <SermonProfileQuestionnaire 
          onComplete={handleSermonProfileComplete}
        />
      )}

      {/* Notes Manager */}
      <NotesManager
        user={user}
        isOpen={showNotesManager}
        onClose={() => setShowNotesManager(false)}
      />

      {/* First-Time User Tutorial */}
      {showTutorial && (
        <AppTutorial onComplete={() => setShowTutorial(false)} />
      )}

      {/* NextStep Navigator */}
      <NextStepNavigator
        isOpen={showNextStepNavigator}
        onClose={() => {
          setShowNextStepNavigator(false);
          setSelectedSermonForNextStep(null);
        }}
        sermon={selectedSermonForNextStep}
        user={user}
        onSermonCreated={(sermon) => {
          fetchSermons();
          toast.success('New sermon created from NextStep!');
        }}
      />

      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        trialStatus={trialStatus}
        canDismiss={!trialStatus?.trial_expired}
      />
    </div>
  );
}